import { Revision } from "@common/utils/redline/ooxml/revision";

export type RedlineSide = "our_side" | "counterparty";

export type OurSideAuthorRule = (authorKey: string) => boolean;

export interface DocumentRedlineAuthor {
  name: string;
  redlineCount: number;
}

export interface RedlineAuthorSide {
  name: string;
  side: RedlineSide;
}

export interface RedlineAuthorColorsSeed {
  currentUserName: string;
  documentAuthors: readonly DocumentRedlineAuthor[];
  isOurSideAuthor: OurSideAuthorRule;
  authorSides?: readonly RedlineAuthorSide[];
}

export const OUR_SIDE_WORD_AUTHOR_COLORS = [
  "#0078D4",
  "#498205",
  "#038387",
  "#7160E8",
  "#005B70",
  "#4E6AED",
  "#0B6A0B",
  "#69797E",
  "#5C2E91",
  "#5D5A58",
  "#394146",
] as const;

export const COUNTERPARTY_WORD_AUTHOR_COLORS = [
  "#CF0F1F",
  "#CC3595",
  "#CA5010",
  "#D13438",
  "#8E562E",
  "#881798",
  "#B146C2",
  "#750B1C",
  "#6D5700",
] as const;

export const WORD_AUTHOR_COLOR_SLOTS: readonly string[] = [
  ...OUR_SIDE_WORD_AUTHOR_COLORS,
  ...[...COUNTERPARTY_WORD_AUTHOR_COLORS].reverse(),
];

const CURRENT_USER_REDLINE_SLOT = 0;
const CURRENT_USER_INSIDE_REDLINE_SLOT = 1;
const FIRST_SHARED_OUR_SIDE_SLOT = 2;
const LAST_SLOT = WORD_AUTHOR_COLOR_SLOTS.length - 1;

type InwardStep = 1 | -1;

interface SidePointer {
  inwardStep: InwardStep;
  outermostReusableSlot: number;
  homePaletteInnermostSlot: number;
  nextFreshSlot: number;
  innermostFreshSlotTaken: number | null;
  bounceSlot: number | null;
  bounceStep: InwardStep;
}

function sidePointerStartingAt(outermostReusableSlot: number, homePaletteInnermostSlot: number, inwardStep: InwardStep): SidePointer {
  return {
    inwardStep,
    outermostReusableSlot,
    homePaletteInnermostSlot,
    nextFreshSlot: outermostReusableSlot,
    innermostFreshSlotTaken: null,
    bounceSlot: null,
    bounceStep: inwardStep,
  };
}

function isWithinSlots(slot: number, firstEnd: number, secondEnd: number): boolean {
  return slot >= Math.min(firstEnd, secondEnd) && slot <= Math.max(firstEnd, secondEnd);
}

function mergedByAuthorKey(documentAuthors: readonly DocumentRedlineAuthor[]): DocumentRedlineAuthor[] {
  const authorsByKey = new Map<string, DocumentRedlineAuthor>();
  for (const author of documentAuthors) {
    const authorKey = Revision.authorKey(author.name);
    const alreadySeen = authorsByKey.get(authorKey);
    if (alreadySeen) {
      alreadySeen.redlineCount += author.redlineCount;
    } else {
      authorsByKey.set(authorKey, { ...author });
    }
  }
  return [...authorsByKey.values()];
}

export class RedlineAuthorColors {
  private readonly currentUserName: string;
  private readonly currentUserKey: string;
  private readonly isOurSideAuthor: OurSideAuthorRule;
  private readonly sideByAuthorKey: Map<string, RedlineSide>;
  private readonly knownAuthors: DocumentRedlineAuthor[];
  private readonly slotByAuthorKey = new Map<string, number>();
  private readonly ourSidePointer = sidePointerStartingAt(FIRST_SHARED_OUR_SIDE_SLOT, OUR_SIDE_WORD_AUTHOR_COLORS.length - 1, 1);
  private readonly counterpartyPointer = sidePointerStartingAt(LAST_SLOT, OUR_SIDE_WORD_AUTHOR_COLORS.length, -1);

  constructor(seed: RedlineAuthorColorsSeed) {
    this.currentUserName = seed.currentUserName;
    this.currentUserKey = Revision.authorKey(seed.currentUserName);
    this.isOurSideAuthor = seed.isOurSideAuthor;
    this.sideByAuthorKey = new Map((seed.authorSides ?? []).map((authorSide) => [Revision.authorKey(authorSide.name), authorSide.side]));
    this.slotByAuthorKey.set(this.currentUserKey, CURRENT_USER_REDLINE_SLOT);
    this.knownAuthors = mergedByAuthorKey(seed.documentAuthors).filter((author) => Revision.authorKey(author.name) !== this.currentUserKey);
    const otherAuthorsBusiestFirst = [...this.knownAuthors].sort((first, second) => second.redlineCount - first.redlineCount);
    for (const side of ["our_side", "counterparty"] as const) {
      for (const author of otherAuthorsBusiestFirst.filter((candidate) => this.sideOf(candidate.name) === side)) {
        this.colorOf(author.name);
      }
    }
  }

  get currentUserRedlineColor(): string {
    return WORD_AUTHOR_COLOR_SLOTS[CURRENT_USER_REDLINE_SLOT];
  }

  get currentUserInsideRedlineColor(): string {
    return WORD_AUTHOR_COLOR_SLOTS[CURRENT_USER_INSIDE_REDLINE_SLOT];
  }

  isCurrentUser(authorName: string): boolean {
    return Revision.authorKey(authorName) === this.currentUserKey;
  }

  sideOf(authorName: string): RedlineSide {
    const authorKey = Revision.authorKey(authorName);
    if (authorKey === this.currentUserKey) return "our_side";
    return this.sideByAuthorKey.get(authorKey) ?? (this.isOurSideAuthor(authorKey) ? "our_side" : "counterparty");
  }

  colorOf(authorName: string): string {
    const authorKey = Revision.authorKey(authorName);
    const assignedSlot = this.slotByAuthorKey.get(authorKey);
    if (assignedSlot !== undefined) return WORD_AUTHOR_COLOR_SLOTS[assignedSlot];
    const newSlot = this.takeSlot(this.sideOf(authorName));
    this.slotByAuthorKey.set(authorKey, newSlot);
    if (!this.knownAuthors.some((author) => Revision.authorKey(author.name) === authorKey)) {
      this.knownAuthors.push({ name: authorName, redlineCount: 0 });
    }
    return WORD_AUTHOR_COLOR_SLOTS[newSlot];
  }

  withAuthorSides(authorSides: readonly RedlineAuthorSide[]): RedlineAuthorColors {
    const keptSides = [...this.sideByAuthorKey].map(([authorKey, side]) => ({ name: authorKey, side }));
    return this.rebuiltWith(this.isOurSideAuthor, [...keptSides, ...authorSides]);
  }

  withOurSideAuthorRule(isOurSideAuthor: OurSideAuthorRule): RedlineAuthorColors {
    return this.rebuiltWith(isOurSideAuthor, [...this.sideByAuthorKey].map(([authorKey, side]) => ({ name: authorKey, side })));
  }

  private rebuiltWith(isOurSideAuthor: OurSideAuthorRule, authorSides: readonly RedlineAuthorSide[]): RedlineAuthorColors {
    return new RedlineAuthorColors({
      currentUserName: this.currentUserName,
      documentAuthors: this.knownAuthors,
      isOurSideAuthor,
      authorSides,
    });
  }

  private takeSlot(side: RedlineSide): number {
    const pointer = side === "our_side" ? this.ourSidePointer : this.counterpartyPointer;
    return this.ourSidePointer.nextFreshSlot <= this.counterpartyPointer.nextFreshSlot
      ? this.takeFreshSlot(pointer)
      : this.takeBouncedSlot(pointer);
  }

  private takeFreshSlot(pointer: SidePointer): number {
    const freshSlot = pointer.nextFreshSlot;
    pointer.nextFreshSlot += pointer.inwardStep;
    pointer.innermostFreshSlotTaken = freshSlot;
    return freshSlot;
  }

  private takeBouncedSlot(pointer: SidePointer): number {
    const innermostSlot = pointer.innermostFreshSlotTaken ?? pointer.homePaletteInnermostSlot;
    if (pointer.bounceSlot === null) {
      pointer.bounceSlot = pointer.innermostFreshSlotTaken ?? pointer.outermostReusableSlot - pointer.inwardStep;
      pointer.bounceStep = pointer.innermostFreshSlotTaken === null ? pointer.inwardStep : (-pointer.inwardStep as InwardStep);
    }
    const nextSlot = pointer.bounceSlot + pointer.bounceStep;
    if (isWithinSlots(nextSlot, pointer.outermostReusableSlot, innermostSlot)) {
      pointer.bounceSlot = nextSlot;
      return nextSlot;
    }
    pointer.bounceStep = -pointer.bounceStep as InwardStep;
    const reversedSlot = pointer.bounceSlot + pointer.bounceStep;
    pointer.bounceSlot = isWithinSlots(reversedSlot, pointer.outermostReusableSlot, innermostSlot) ? reversedSlot : pointer.bounceSlot;
    return pointer.bounceSlot;
  }
}
